import { useMutation } from "@tanstack/react-query";
import {
  CheckBox,
  IndexPath,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import Button from "components/Button";
import Spacer from "components/Spacer";
import {
  EventAvailabilityDetails,
  EventDetails,
  TransferTicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import useTDCDialog from "hooks/useTDCDialog";
import {
  transferTicketFetcher,
  useGetTransferDetailsQuery,
} from "queries/ticket.queries";
import React, { useEffect, useState } from "react";
import { ScrollView, View } from "react-native";
import TDCDialog from "screens/Schedule/components/TDCDialog";
import accountServices from "services/account.services";
import { toDashboard } from "utils/navigationUtils";
import toast from "utils/toast";

interface StepTwoProps {
  transferCode: string;
  eventCode: string;
  event: EventDetails;
}

const getTicketLabel = (ticket: TransferTicketDetails): string => {
  const { seat } = ticket;
  return `Sec ${seat.sectionDisplay} · Row ${seat.rowDisplay} · Seat ${seat.seatDisplay}`;
};

const StepTwo: React.FC<StepTwoProps> = (props) => {
  const { transferCode, eventCode, event } = props;
  const [selectedIndex, setSelectedIndex] = useState<IndexPath>(
    new IndexPath(0),
  );
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(
    new Set(),
  );
  const { TDCDialogVisible, setTDCDialogVisible, withTDCCheck } =
    useTDCDialog();
  const userId = useAppSelector((state) => state.auth.id);

  const { account, updateFetchAccountBffCache } = useAccount();
  const {
    data: transferDetails,
    error,
    refetch: refetchTransferDetails,
  } = useGetTransferDetailsQuery({
    eventCode,
    transferCode,
  });
  const { mutateAsync: createAccount, isLoading: creatingAccount } =
    useMutation(accountServices.createAccount);
  const { mutateAsync: transfer, isLoading } = useMutation(
    transferTicketFetcher,
  );

  const hasSpecificTickets =
    transferDetails?.selectedTickets != null &&
    transferDetails.selectedTickets.length > 0;

  const allSelected =
    hasSpecificTickets &&
    selectedTicketIds.size === transferDetails!.selectedTickets.length;

  const toggleTicket = (ticketId: number) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedTicketIds(new Set());
    } else {
      setSelectedTicketIds(
        new Set(transferDetails!.selectedTickets.map((t) => t.id)),
      );
    }
  };

  const handleSubmit = async () => {
    withTDCCheck(event, async () => {
      if (hasSpecificTickets) {
        await transfer({
          eventCode,
          transferCode,
          ticketIds: Array.from(selectedTicketIds),
        });
      } else {
        await transfer({
          eventCode,
          transferCode,
          quantity: selectedIndex.row + 1,
        });
      }
      toast.success("You have successfully claimed your seat");
      toDashboard();
    });
  };

  useEffect(() => {
    if (!transferDetails) return;
    if (hasSpecificTickets) {
      // Pre-select all specifically designated tickets
      setSelectedTicketIds(
        new Set(transferDetails.selectedTickets.map((t) => t.id)),
      );
    } else {
      setSelectedIndex(new IndexPath(transferDetails.remainingClaimable - 1));
    }
  }, [transferDetails]);

  // If user does not have a subscription for current tenant(but does for another tenant), register them as associate account
  if (error?.data.code === "ACCOUNT_NOT_FOUND" && !creatingAccount) {
    createAccount({
      type: "ASSOCIATE",
      primaryUser: {
        userId: userId!,
        planPackageIds: [],
      },
      source: "FANRALLY_APP",
      emailMarketingConsent: false,
      smsMarketingConsent: false,
    }).then((account) => {
      updateFetchAccountBffCache(account);
      refetchTransferDetails();
    });
  }

  return (
    <View style={{ marginVertical: 15 }}>
      {transferDetails !== undefined && (
        <View>
          <View style={{ marginVertical: 15 }}>
            {transferDetails.remainingClaimable > 0 ? (
              <>
                {hasSpecificTickets ? (
                  <View>
                    <Text category="s1" style={{ marginBottom: 8 }}>
                      The following tickets are being transferred to you:
                    </Text>
                    <CheckBox
                      checked={allSelected}
                      indeterminate={selectedTicketIds.size > 0 && !allSelected}
                      onChange={toggleAll}
                      style={{ marginBottom: 8 }}
                    >
                      Select All
                    </CheckBox>
                    <ScrollView style={{ maxHeight: 200 }}>
                      {transferDetails.selectedTickets.map((ticket) => (
                        <CheckBox
                          key={ticket.id}
                          checked={selectedTicketIds.has(ticket.id)}
                          onChange={() => toggleTicket(ticket.id)}
                          style={{ marginVertical: 4 }}
                        >
                          {getTicketLabel(ticket)}
                        </CheckBox>
                      ))}
                    </ScrollView>
                  </View>
                ) : (
                  <Select
                    label={`How many seats would you like to claim? (Maximum ${transferDetails.remainingClaimable})`}
                    onSelect={(index) => setSelectedIndex(index as IndexPath)}
                    selectedIndex={selectedIndex}
                    value={selectedIndex.row + 1}
                  >
                    {new Array(transferDetails.remainingClaimable)
                      .fill(0)
                      .map((_, index) => (
                        <SelectItem key={index} title={index + 1} />
                      ))}
                  </Select>
                )}
                <Spacer />
                <Button
                  onPress={handleSubmit}
                  loading={isLoading}
                  disabled={hasSpecificTickets && selectedTicketIds.size === 0}
                >
                  Claim
                </Button>
              </>
            ) : (
              <Text status="danger">
                All of the tickets for this transfer code have already been
                claimed.
              </Text>
            )}
          </View>

          {TDCDialogVisible && (
            <TDCDialog
              visible={TDCDialogVisible}
              setVisible={setTDCDialogVisible}
              event={event as unknown as EventAvailabilityDetails}
            />
          )}
        </View>
      )}
    </View>
  );
};

export default StepTwo;
